import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useHasPermission } from '../../hooks/usePermission';
import { getReports } from '../../redux/slices/creditSlice';
import ForbiddenPage from '../ForbiddenPage';

/**
 * CreditReportsPage - Credit reports page
 * Phase 7: Credit System
 * 
 * Features:
 * - Total credit sales (TL)
 * - Total credits used
 * - Document-based cost reports (totalCostTL, creditPriceAtCompletion)
 * - User/Company summary reports
 * - Date range filtering
 * 
 * Permission: credits.reports
 * Backend scope filtering: System admin (all), Company owner (own company), Others (own transactions)
 */
const CreditReportsPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const hasPermission = useHasPermission('credits.reports');

  const { reports, loading, error } = useSelector(state => state.credits);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (hasPermission) {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      dispatch(getReports(params));
    }
  }, [dispatch, hasPermission, startDate, endDate]);

  const handleBack = () => {
    history.goBack();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount || 0);
  };

  if (!hasPermission) {
    return <ForbiddenPage />;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Geri Dön
          </Button>
          <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Kredi Raporları
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Kredi satışları, kullanımları ve maliyet raporları
            </Typography>
          </Box>
        </Box>

        {/* Date Range Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Tarih Aralığı
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Başlangıç Tarihi"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Bitiş Tarihi"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && !reports && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Summary Cards */}
        {reports && (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Toplam Kredi Satışı
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {reports.totalSales || 0} Kredi
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Kredi kartı ile satın alınan
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Toplam Yüklenen Kredi
                    </Typography>
                    <Typography variant="h4" color="info.main">
                      {reports.totalCreditsLoaded || 0} Kredi
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Satın alınan + Manuel yüklenen
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Toplam Kullanılan Kredi
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {reports.totalUsed || 0} Kredi
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Doküman tamamlamalarında kullanıldı
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Document-Based Cost Reports */}
            {reports.documents && reports.documents.length > 0 && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Doküman Bazlı Maliyet Raporları
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Her dokümanın tamamlandığı zamandaki kredi fiyatı ile hesaplanan maliyetleri
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Doküman</strong></TableCell>
                        <TableCell><strong>Kullanılan Kredi</strong></TableCell>
                        <TableCell><strong>Kredi Fiyatı (O Zaman)</strong></TableCell>
                        <TableCell><strong>Toplam Maliyet (TL)</strong></TableCell>
                        <TableCell><strong>Tarih</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reports.documents.map((doc) => (
                        <TableRow key={doc._id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {doc.title || 'Başlıksız Doküman'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${doc.creditsUsed || 0} Kredi`}
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {doc.creditPriceAtCompletion 
                              ? formatCurrency(doc.creditPriceAtCompletion)
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" color="error.main">
                              {doc.totalCostTL ? formatCurrency(doc.totalCostTL) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {doc.completedAt
                              ? format(new Date(doc.completedAt), 'dd MMM yyyy HH:mm', { locale: tr })
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* User/Company Summary Reports */}
            {reports.users && reports.users.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Kullanıcı/Şirket Özet Raporları
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Kullanıcı/Şirket</strong></TableCell>
                        <TableCell><strong>Toplam Satın Alınan</strong></TableCell>
                        <TableCell><strong>Toplam Manuel Yüklenen</strong></TableCell>
                        <TableCell><strong>Toplam Yüklenen</strong></TableCell>
                        <TableCell><strong>Toplam Kullanılan</strong></TableCell>
                        <TableCell><strong>Mevcut Bakiye</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reports.users.map((user, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            {user.user ? (
                              <Typography variant="body2">
                                {user.user.firstName} {user.user.lastName || ''} ({user.user.email})
                              </Typography>
                            ) : user.company ? (
                              <Typography variant="body2" fontWeight="medium">
                                {user.company.companyName || user.company.name}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Bilinmeyen
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="success.main" fontWeight="bold">
                              {user.totalPurchased || 0} Kredi
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="info.main" fontWeight="bold">
                              {user.totalManualAdded || 0} Kredi
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="primary.main" fontWeight="bold">
                              {user.totalCreditsLoaded || 0} Kredi
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="error.main" fontWeight="bold">
                              {user.totalUsed || 0} Kredi
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {user.balance || 0} Kredi
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* No Data Message */}
            {(!reports.documents || reports.documents.length === 0) && 
             (!reports.users || reports.users.length === 0) && (
              <Alert severity="info">
                Seçilen tarih aralığında veri bulunamadı
              </Alert>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default CreditReportsPage;

